/**
 * Converts a JavaScript value to a JSON string with custom formatting for nested objects/arrays.
 * Specifically, it identifies "leaf" objects (objects whose direct properties are not arrays or other objects)
 * and stringifies them. Subsequent replace operations aim to make these embedded structures
 * (including arrays within them) appear without the outer quotes and with minimal escaping.
 *
 * @param obj - The value to convert to a JSON string. Can be any JSON-serializable value.
 * @param indentProvided - The number of spaces to use for indentation. Defaults to 2.
 * @returns A formatted JSON string.
 */
export const jsonPretty = (obj: unknown, indentProvided?: number): string => {
  const indent: number =
    indentProvided === undefined || indentProvided === null ? 2 : indentProvided

  // Type for the replacer function's parameters
  type JsonReplacer = (this: unknown, key: string, value: unknown) => unknown

  const replacer: JsonReplacer = function (k: string, v: unknown): unknown {
    // Check if this is a leaf-object with no child Arrays or Objects:
    // Your original logic considered an object a "leaf" if *none* of its properties
    // were themselves arrays or objects.
    if (typeof v === "object" && v !== null) {
      // Ensure it's an object and not null
      const obj = v as Record<string, unknown>
      let isLeafObject = true // Assume it's a leaf initially
      for (const p in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, p)) {
          // If any property is an array or an object, it's not a leaf object
          if (
            Array.isArray(obj[p]) ||
            (typeof obj[p] === "object" && obj[p] !== null)
          ) {
            isLeafObject = false
            break
          }
        }
      }
      if (isLeafObject) {
        // If it's a leaf object (even if it contains arrays of primitives), stringify it.
        return JSON.stringify(obj)
      }
    }
    // For primitives, arrays, or objects that are not "leaf" objects,
    // return them as is for the outer JSON.stringify to handle.
    return v
  }

  const initialJsonString = JSON.stringify(obj, replacer, indent)

  // Your original replace calls, designed to clean up the output of
  // stringified "leaf" objects (which might contain arrays of primitives).
  return initialJsonString
    .replace(/\\/g, "") // Remove general escape characters (this was broad, might need refinement if strings contain legitimate escaped chars)
    .replace(/"\[/g, "[") // Remove quotes before an opening array bracket: "[ -> [
    .replace(/\]"/g, "]") // Remove quotes after a closing array bracket: ]" -> ]
    .replace(/"\{/g, "{") // Remove quotes before an opening object bracket: "{ -> {
    .replace(/\}"/g, "}") // Remove quotes after a closing object bracket: }" -> }
}
